import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const PayeeFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
}) => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    type: "individual",
    vat_no: "",
    swift_bic: "",
    iban: "",
    address_1: "",
    country: "",
    city: "",
    postcode: "",
  });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        email: initialData.email || "",
        type: initialData.type || "individual",
        name: initialData.name || "",
        swift_bic: initialData.swift_bic || "",
        iban: initialData.iban || "",
        vat_no: initialData.vat_no || "",
        country: initialData.address?.country || "",
        city: initialData.address?.city || "",
        address_1: initialData.address?.address_1 || "",
        postcode: initialData.address?.postcode || "",
      });
    }

    if (mode === "create") {
      setForm({
        email: "",
        name: "",
        type: "individual",
        swift_bic: "",
        iban: "",
        vat_no: "",
        address_1: "",
        country: "",
        city: "",
        postcode: "",
      });
    }
  }, [mode, initialData]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Payee" : "Add Payee"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Email*</label>
            <input
              type="text"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Select Payee Type*
            </label>
            <select
              value={form.type}
              onChange={(e) => handleChange("type", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              <option value="individual">Personal</option>
              <option value="business">Business</option>
            </select>
          </div>

          {form.type === "business" && (
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                VAT/ CIF
              </label>
              <input
                type="text"
                value={form.vat_no}
                onChange={(e) => handleChange("vat_no", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Payee Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Swift Code (BIC)*
              </label>
              <input
                type="text"
                value={form.swift_bic}
                onChange={(e) => handleChange("swift_bic", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">IBAN*</label>
              <input
                type="text"
                value={form.iban}
                onChange={(e) => handleChange("iban", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Address*
              </label>
              <input
                type="text"
                value={form.address_1}
                onChange={(e) => handleChange("address_1", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">City*</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Country of Residence*
              </label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => handleChange("country", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                PostCode*
              </label>
              <input
                type="text"
                value={form.postcode}
                onChange={(e) => handleChange("postcode", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading
              ? "Saving..."
              : mode === "edit"
                ? "Update Payee"
                : "Create Payee"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayeeFormModal;
